import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Sparkles,
  Mic,
  CheckCircle2,
  Users,
  Layers,
  FileText,
  FolderKanban,
  Lightbulb,
  AlertTriangle,
  GitMerge,
  TrendingUp,
  Shield,
  Zap,
} from 'lucide-react';
import { Button, AnimatedBackground, MarketingHero, ErrorBanner } from '@/components/ui';
import { useTranslation } from 'react-i18next';
import heroLogo from './newLogo.png';
import { usePlans } from '@/contexts/PlansContext';

/** Map plan colorTheme to pricing card styling */
const PRICING_CARD_STYLES: Record<string, { border: string; bg: string; shadow: string; badge: string; cta: string }> = {
  slate: {
    border: 'border border-slate-200',
    bg: 'bg-white',
    shadow: '',
    badge: 'bg-emerald-100 text-emerald-600',
    cta: 'bg-emerald-600 hover:bg-emerald-500',
  },
  blue: {
    border: 'border-2 border-blue-400',
    bg: 'bg-gradient-to-b from-blue-50 to-white',
    shadow: 'shadow-lg shadow-blue-100',
    badge: 'bg-blue-500 text-white',
    cta: 'bg-blue-600 hover:bg-blue-500',
  },
  amber: {
    border: 'border-2 border-amber-400',
    bg: 'bg-gradient-to-b from-amber-50 to-white',
    shadow: 'shadow-lg shadow-amber-100',
    badge: '',
    cta: 'bg-amber-600 hover:bg-amber-500',
  },
  purple: {
    border: 'border border-slate-200',
    bg: 'bg-white',
    shadow: '',
    badge: 'bg-slate-900 text-white',
    cta: '',
  },
};

const IntroSection: React.FC = () => {
  const { t } = useTranslation();
  const { plans, getPlanPrice, error: plansError, refreshPlans } = usePlans();
  const coreCapabilities = [
    {
      icon: Mic,
      title: t('home.capRecordTitle'),
      desc: t('home.capRecordDesc'),
      color: 'red',
      features: [t('home.capRecordFeature1'), t('home.capRecordFeature2'), t('home.capRecordFeature3')],
      link: '/add'
    },
    {
      icon: FileText,
      title: t('home.capTemplateTitle'),
      desc: t('home.capTemplateDesc'),
      color: 'blue',
      features: [t('home.capTemplateFeature1'), t('home.capTemplateFeature2'), t('home.capTemplateFeature3')],
      link: '/project-settings'
    },
    {
      icon: FolderKanban,
      title: t('home.capOrganizeTitle'),
      desc: t('home.capOrganizeDesc'),
      color: 'emerald',
      features: [t('home.capOrganizeFeature1'), t('home.capOrganizeFeature2'), t('home.capOrganizeFeature3')],
      link: '/project-settings'
    },
    {
      icon: Lightbulb,
      title: t('home.capInsightsTitle'),
      desc: t('home.capInsightsDesc'),
      color: 'purple',
      features: [t('home.capInsightsFeature1'), t('home.capInsightsFeature2'), t('home.capInsightsFeature3')],
      link: '/insights'
    }
  ];

  const painPoints = [
    {
      icon: AlertTriangle,
      problem: t('home.painScatteredProblem'),
      solution: t('home.painScatteredSolution'),
    },
    {
      icon: GitMerge,
      problem: t('home.painReinventingProblem'),
      solution: t('home.painReinventingSolution'),
    },
    {
      icon: Shield,
      problem: t('home.painInconsistentProblem'),
      solution: t('home.painInconsistentSolution'),
    },
    {
      icon: TrendingUp,
      problem: t('home.painLostProblem'),
      solution: t('home.painLostSolution'),
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <MarketingHero variant="hero" intensity="normal" className="pt-20 pb-24 sm:pt-24">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-500/20 via-transparent to-transparent" />

        <div className="relative container mx-auto px-6 max-w-6xl">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/20 text-blue-300 rounded-full text-sm font-medium mb-6 border border-blue-400/30">
              <Sparkles className="w-4 h-4" />
              {t('home.heroBadge')}
            </span>

            {/* Hero Headline */}
            <div className="mb-6">
              <img src={heroLogo} alt="SystemDox" className="h-24 sm:h-32 lg:h-40 mx-auto" />
              <p className="text-xl sm:text-2xl text-slate-300 mt-4 font-light tracking-wide">
                {t('home.heroSlogan', 'The clever way of documenting your project')}
              </p>
            </div>

            {/* Subheadline - EXACT flow as specified */}
            <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 text-lg sm:text-xl text-slate-300 mb-10">
              <span>{t('home.flowRecord')}</span>
              <ArrowRight className="w-4 h-4 text-blue-400" />
              <span>{t('home.flowAutoTranscribe')}</span>
              <ArrowRight className="w-4 h-4 text-blue-400" />
              <span>{t('home.flowApplyTemplates')}</span>
              <ArrowRight className="w-4 h-4 text-blue-400" />
              <span>{t('home.flowOrganizeInProjects')}</span>
              <ArrowRight className="w-4 h-4 text-blue-400" />
              <span className="text-cyan-400 font-semibold">{t('home.flowGetInsights')}</span>
            </div>

            {/* CTA - EXACT as specified */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button asChild variant="gradient" size="xl" shape="rounded" className="group">
                <Link to="/signup">
                  <Mic className="w-5 h-5" />
                  {t('home.ctaStartFreeTrial')}
                  <ArrowRight
                    size={20}
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </Link>
              </Button>
              <p className="text-sm text-slate-400">{t('home.ctaTrialNote')}</p>
            </div>
          </div>

          {/* Value highlight strip */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: t('home.valueSpotDuplications'), desc: t('home.valueSpotDuplicationsDesc') },
              { label: t('home.valueUnifyDecisions'), desc: t('home.valueUnifyDecisionsDesc') },
              { label: t('home.valueEvolveSmarter'), desc: t('home.valueEvolveSmarterDesc') },
              { label: t('home.valuePreventDivergence'), desc: t('home.valuePreventDivergenceDesc') },
            ].map((item, i) => (
              <div key={i} className="text-center p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm">
                <p className="text-white font-semibold">{item.label}</p>
                <p className="text-slate-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </MarketingHero>

      {/* Core Capabilities - Full Flow */}
      <section className="relative py-20 overflow-hidden">
        <AnimatedBackground variant="light" intensity="subtle" />
        <div className="relative container mx-auto px-6 max-w-6xl">
          <div className="text-center mb-16">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4">
              <Zap className="w-4 h-4" />
              {t('home.capabilitiesBadge')}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {t('home.capabilitiesTitle')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('home.capabilitiesSubtitle')}
            </p>
          </div>

          {/* Flow indicator */}
          <div className="flex items-center justify-center gap-2 sm:gap-4 mb-12 flex-wrap">
            {[t('home.flowStepRecord'), t('home.flowStepTemplate'), t('home.flowStepOrganize'), t('home.flowStepInsights')].map((label, i) => (
              <React.Fragment key={i}>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    i === 3 ? 'bg-purple-100' : i === 2 ? 'bg-emerald-100' : i === 1 ? 'bg-blue-100' : 'bg-red-100'
                  }`}>
                    <span className={`font-bold ${
                      i === 3 ? 'text-purple-600' : i === 2 ? 'text-emerald-600' : i === 1 ? 'text-blue-600' : 'text-red-600'
                    }`}>{i + 1}</span>
                  </div>
                  <span className="hidden sm:inline">{label}</span>
                </div>
                {i < 3 && <ArrowRight className="w-4 h-4 text-slate-300" />}
              </React.Fragment>
            ))}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {coreCapabilities.map((capability, i) => {
              const colorClasses: Record<string, { bg: string; bgHover: string; text: string; border: string }> = {
                red: { bg: 'bg-red-100', bgHover: 'group-hover:bg-red-200', text: 'text-red-600', border: 'hover:border-red-300' },
                emerald: { bg: 'bg-emerald-100', bgHover: 'group-hover:bg-emerald-200', text: 'text-emerald-600', border: 'hover:border-emerald-300' },
                purple: { bg: 'bg-purple-100', bgHover: 'group-hover:bg-purple-200', text: 'text-purple-600', border: 'hover:border-purple-300' },
                blue: { bg: 'bg-blue-100', bgHover: 'group-hover:bg-blue-200', text: 'text-blue-600', border: 'hover:border-blue-300' },
                amber: { bg: 'bg-amber-100', bgHover: 'group-hover:bg-amber-200', text: 'text-amber-600', border: 'hover:border-amber-300' },
              };
              const colors = colorClasses[capability.color];

              const CardContent = (
                <>
                  <div className={`mb-4 inline-flex items-center justify-center w-12 h-12 rounded-xl transition-colors ${colors.bg} ${colors.bgHover}`}>
                    <capability.icon size={24} className={colors.text} />
                  </div>
                  <h3 className="text-lg font-semibold mb-2 text-slate-900 group-hover:text-blue-700 transition-colors">
                    {capability.title}
                    {capability.link && (
                      <ArrowRight size={16} className="inline ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                    )}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-4">{capability.desc}</p>
                  <ul className="space-y-1.5">
                    {capability.features.map((feature, j) => (
                      <li key={j} className="flex items-center gap-2 text-slate-600 text-xs">
                        <CheckCircle2 size={14} className="text-emerald-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </>
              );

              const cardClasses = `group p-6 bg-white border border-slate-200 rounded-xl ${colors.border} hover:shadow-lg transition-all duration-300`;

              return capability.link ? (
                <Link key={i} to={capability.link} className={cardClasses}>
                  {CardContent}
                </Link>
              ) : (
                <div key={i} className={cardClasses}>
                  {CardContent}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Insights Highlight Section */}
      <section className="py-20 bg-gradient-to-br from-amber-50 via-white to-orange-50">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-medium mb-4">
                <Lightbulb className="w-4 h-4" />
                {t('home.insightsBadge')}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                {t('home.insightsTitle')}
                <br />
                <span className="text-amber-600">{t('home.insightsTitleHighlight')}</span>
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                {t('home.insightsSubtitle')}
              </p>
              <ul className="space-y-4">
                {[
                  { title: t('home.insightDuplicationTitle'), desc: t('home.insightDuplicationDesc') },
                  { title: t('home.insightConsistencyTitle'), desc: t('home.insightConsistencyDesc') },
                  { title: t('home.insightOptimizationTitle'), desc: t('home.insightOptimizationDesc') },
                  { title: t('home.insightKnowledgeTitle'), desc: t('home.insightKnowledgeDesc') },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{item.title}</p>
                      <p className="text-slate-600 text-sm">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 shadow-xl p-6">
              <div className="border-b border-slate-100 pb-4 mb-4">
                <p className="text-sm text-slate-500 mb-1">{t('home.insightDemoAnalysis')}</p>
                <p className="font-semibold text-slate-900">{t('home.insightDemoFound')}</p>
              </div>
              <div className="space-y-4">
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <div className="flex items-center gap-2 text-amber-700 font-medium mb-1">
                    <AlertTriangle className="w-4 h-4" />
                    {t('home.insightDemoDuplicateTitle')}
                  </div>
                  <p className="text-sm text-amber-600">
                    {t('home.insightDemoDuplicateDesc')}
                  </p>
                </div>
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                  <div className="flex items-center gap-2 text-blue-700 font-medium mb-1">
                    <GitMerge className="w-4 h-4" />
                    {t('home.insightDemoInconsistencyTitle')}
                  </div>
                  <p className="text-sm text-blue-600">
                    {t('home.insightDemoInconsistencyDesc')}
                  </p>
                </div>
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                  <div className="flex items-center gap-2 text-emerald-700 font-medium mb-1">
                    <TrendingUp className="w-4 h-4" />
                    {t('home.insightDemoOptimizationTitle')}
                  </div>
                  <p className="text-sm text-emerald-600">
                    {t('home.insightDemoOptimizationDesc')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-16 bg-white border-b border-slate-200">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              {t('home.whoTitle')}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {t('home.whoSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-6 rounded-xl bg-slate-50 border border-slate-200">
              <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">{t('home.whoArchitectsTitle')}</h3>
              <p className="text-slate-600 text-sm">
                {t('home.whoArchitectsDesc')}
              </p>
            </div>
            <div className="p-6 rounded-xl bg-slate-50 border border-slate-200">
              <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center mb-4">
                <Layers className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">{t('home.whoTeamsTitle')}</h3>
              <p className="text-slate-600 text-sm">
                {t('home.whoTeamsDesc')}
              </p>
            </div>
            <div className="p-6 rounded-xl bg-slate-50 border border-slate-200">
              <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center mb-4">
                <Sparkles className="w-6 h-6 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">{t('home.whoConsultantsTitle')}</h3>
              <p className="text-slate-600 text-sm">
                {t('home.whoConsultantsDesc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Grid */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium mb-4">
              <Layers className="w-4 h-4" />
              {t('home.pricingBadge')}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">
              {t('pricing.simplePlans')}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {t('pricing.startFreeThenUpgrade')}
            </p>
          </div>

          {plansError ? (
            <ErrorBanner message={plansError} onRetry={refreshPlans} className="max-w-md mx-auto" />
          ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan) => {
              const style = PRICING_CARD_STYLES[plan.display.colorTheme] || PRICING_CARD_STYLES.slate;
              const isOrg = plan.planId === 'organization';
              const isFree = plan.planId === 'free';
              const starterPlan = plans.find(p => p.planId === 'starter');

              return (
                <div key={plan.planId} className={`${style.border} rounded-2xl p-6 flex flex-col ${style.bg} ${style.shadow}`}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-slate-900">{plan.displayName}</h3>
                    {isFree && (
                      <span className="text-xs font-medium px-2 py-1 rounded-full bg-emerald-100 text-emerald-600">
                        {t('pricing.getStarted')}
                      </span>
                    )}
                    {plan.display.recommended && style.badge && (
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${style.badge}`}>
                        {t('pricing.mostPopular')}
                      </span>
                    )}
                    {isOrg && (
                      <span className="text-xs font-medium px-2 py-1 rounded-full bg-slate-900 text-white">
                        {t('pricing.enterprise')}
                      </span>
                    )}
                  </div>
                  <p className="text-3xl font-bold text-slate-900 mb-1">{getPlanPrice(plan.planId)}</p>
                  <p className="text-sm text-slate-500 mb-4">{plan.pricePerMonth}</p>
                  {isFree && starterPlan && (
                    <p className="text-xs text-slate-500 mb-2">Then {getPlanPrice('starter')}/month after trial</p>
                  )}
                  <ul className={`space-y-3 text-sm mb-6 flex-1 ${plan.display.colorTheme === 'slate' || isOrg ? 'text-slate-600' : 'text-slate-700'}`}>
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  {isOrg ? (
                    <Button asChild variant="outline" size="md" shape="rounded" fullWidth className="mt-auto border-slate-300 text-slate-800 hover:bg-slate-100 hover:text-slate-800">
                      <Link to="/contact">{t('common.contactSales')}</Link>
                    </Button>
                  ) : (
                    <Button asChild variant="success" size="md" shape="rounded" fullWidth className={`mt-auto ${style.cta}`}>
                      <Link to="/signup">{t('common.startFreeRecording')}</Link>
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
          )}
        </div>
      </section>

      {/* Final CTA Section */}
      <MarketingHero variant="cta" intensity="normal" className="py-20">
        <div className="container mx-auto px-6 max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t('home.finalCtaTitle')}
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            {t('home.finalCtaSubtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button asChild variant="gradient" size="xl" shape="rounded" className="group">
              <Link to="/add">
                <Mic className="w-5 h-5" />
                {t('home.ctaStartFreeTrial')}
                <ArrowRight
                  size={20}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </Link>
            </Button>
            <p className="text-sm text-slate-400">{t('home.ctaTrialNote')}</p>
          </div>
        </div>
      </MarketingHero>
    </div>
  );
};

export default IntroSection;
